/**
 * DevToolsOverlay
 *
 * Super-admin developer panel ("Dev Console"), opened from the header menu.
 * The header dispatches `toggle-dev-console`; this listens for it. It replaces
 * the old type-"dev"-anywhere trigger, which depended on a keystroke buffer
 * that never fired reliably and gave no clue it existed when it stopped.
 * Provides quick-access buttons for estimate data management functions.
 * Press Escape or click ✕ to close.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  dumpEstimateData,
  saveEstimateData,
  loadEstimateData,
  performSave,
  registerSavePrompt,
  registerLoadConfirm,
} from '../../services/estimateDataManager';
import type { LoadConfirmInfo, SavePromptOptions } from '../../services/estimateDataManager';
import { clearSplashSeen } from '../../utils/splashSeen';
import { useResultsUnlocked, setResultsUnlocked } from '../../utils/devUnlockResults';
import { SaveModal } from './SaveModal';
import { LoadModal } from './LoadModal';
import { useAuth } from '../../context/AuthContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useEstimator } from '../../context/EstimatorContext';
import { LightingPanel } from './LightingPanel';
import { buildVisualizerPayload, buildVisualizerUrl } from '../results/visualizerPayload';
import './DevToolsOverlay.css';

export const DevToolsOverlay: React.FC = () => {
  const { isAuthenticated, login, logout } = useAuth();
  const { isSuperAdmin } = useAdminAuth();
  const { state } = useEstimator();
  const hasEstimate = state.estimateResult !== null;
  const [visible, setVisible] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  // Options forwarded from the active saveEstimateData(options) caller.
  // Shared-estimate save uses these to override the pre-fill + save callback
  // without bypassing the modal UI.
  const [saveOptions, setSaveOptions] = useState<SavePromptOptions | null>(null);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [loadInfo, setLoadInfo] = useState<LoadConfirmInfo | null>(null);
  const loadResolveRef = useRef<((confirmed: boolean) => void) | null>(null);
  const [lightingPanelOpen, setLightingPanelOpen] = useState(false);
  const resultsUnlocked = useResultsUnlocked();

  // Register the save prompt so saveEstimateData() opens our modal
  useEffect(() => {
    registerSavePrompt((options?: SavePromptOptions) => {
      setSaveOptions(options ?? null);
      setSaveModalOpen(true);
    });
  }, []);

  // Register the load confirm so loadEstimateData() opens our modal
  useEffect(() => {
    registerLoadConfirm((info: LoadConfirmInfo) => {
      return new Promise<boolean>((resolve) => {
        setLoadInfo(info);
        setLoadModalOpen(true);
        loadResolveRef.current = resolve;
      });
    });
  }, []);

  const handleSaveConfirm = useCallback((projectName: string) => {
    setSaveModalOpen(false);
    if (saveOptions?.onSave) {
      saveOptions.onSave(projectName);
    } else {
      performSave(projectName);
    }
    setSaveOptions(null);
  }, [saveOptions]);

  const handleSaveCancel = useCallback(() => {
    setSaveModalOpen(false);
    setSaveOptions(null);
  }, []);

  const handleLoadConfirm = useCallback(() => {
    setLoadModalOpen(false);
    loadResolveRef.current?.(true);
    loadResolveRef.current = null;
  }, []);

  const handleLoadCancel = useCallback(() => {
    setLoadModalOpen(false);
    loadResolveRef.current?.(false);
    loadResolveRef.current = null;
  }, []);

  // Header menu → "Dev Console". Mounted for every visitor (the save/load
  // modals below have to be), so the render gate below is what keeps the panel
  // super-admin-only; the listener itself is harmless.
  useEffect(() => {
    const onToggle = () => setVisible(v => !v);
    window.addEventListener('toggle-dev-console', onToggle);
    return () => window.removeEventListener('toggle-dev-console', onToggle);
  }, []);

  // Escape closes the panel while it is open.
  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible]);

  const handleDump = useCallback(() => {
    dumpEstimateData();
    console.log('%c↑ Check the console above for the full data object', 'color:#6b7280;font-style:italic');
  }, []);

  const handleOpenBitmapVisualizer = useCallback(() => {
    if (!state.estimateResult) return;
    const payload = buildVisualizerPayload(state, state.estimateResult);
    const url = buildVisualizerUrl(payload);
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [state]);

  return (
    <>
      {/* Save / Load modals must render for EVERY visitor — the main header
          menu's "Save Current Config" / "Load a Local Config" depend on them.
          They previously sat below an `if (!isAdminAuthenticated) return null`
          gate, so for non-admin users the modal state flipped but the modal
          was never mounted: saving silently did nothing (and threw no error). */}
      <SaveModal
        open={saveModalOpen}
        onSave={handleSaveConfirm}
        onCancel={handleSaveCancel}
        defaultName={saveOptions?.defaultName}
      />
      <LoadModal open={loadModalOpen} info={loadInfo} onConfirm={handleLoadConfirm} onCancel={handleLoadCancel} />

      {/* Everything below is super-admin-only dev tooling. */}
      {isSuperAdmin && lightingPanelOpen && <LightingPanel onClose={() => setLightingPanelOpen(false)} />}

      {isSuperAdmin && visible && (
        <div className="devtools-overlay">
          <div className="devtools-header">
            <span className="devtools-title">🛠️ Dev Console</span>
            <button
              className="devtools-close"
              onClick={() => setVisible(false)}
              title="Close (Esc)"
            >
              ✕
            </button>
          </div>
          <div className="devtools-buttons">
            <button className="devtools-btn devtools-btn--dump" onClick={handleDump}>
              📋 Dump Data
              <span className="devtools-btn-hint">Console log current state</span>
            </button>
            <button className="devtools-btn devtools-btn--save" onClick={() => saveEstimateData()}>
              💾 Save Config
              <span className="devtools-btn-hint">Download as JSON file</span>
            </button>
            <button className="devtools-btn devtools-btn--load" onClick={() => loadEstimateData()}>
              📂 Load Config
              <span className="devtools-btn-hint">Load from JSON file</span>
            </button>
            <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.15)', margin: '4px 0' }} />
            <button
              className={`devtools-btn ${isAuthenticated ? 'devtools-btn--save' : 'devtools-btn--dump'}`}
              onClick={() => (isAuthenticated ? logout() : login())}
            >
              {isAuthenticated ? '🔓 Logged In' : '🔒 Guest'}
              <span className="devtools-btn-hint">
                {isAuthenticated ? 'Click to sign out (Keycloak)' : 'Click to sign in (Keycloak)'}
              </span>
            </button>
            <button
              className={`devtools-btn ${resultsUnlocked ? 'devtools-btn--save' : 'devtools-btn--dump'}`}
              onClick={() => setResultsUnlocked(!resultsUnlocked)}
            >
              {resultsUnlocked ? '👁️ Full Results: ON' : '🙈 Full Results: OFF'}
              <span className="devtools-btn-hint">
                {resultsUnlocked
                  ? 'Gate open, save prompts suppressed — click for the real view'
                  : 'Show the whole estimate, no forced save prompt'}
              </span>
            </button>
            <button
              className="devtools-btn devtools-btn--dump"
              onClick={() => { sessionStorage.clear(); clearSplashSeen(); alert('Session cleared — splash will show on next load'); }}
            >
              🧹 Clear Session
              <span className="devtools-btn-hint">Reset splash & session data</span>
            </button>
            <button
              className="devtools-btn devtools-btn--load"
              onClick={() => window.dispatchEvent(new Event('show-splash'))}
            >
              🖼️ Show Splash
              <span className="devtools-btn-hint">Display splash overlay</span>
            </button>
            <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.15)', margin: '4px 0' }} />
            <button
              className="devtools-btn devtools-btn--save"
              onClick={() => window.location.href = '/admin'}
            >
              ⚙️ Admin Panel
              <span className="devtools-btn-hint">Open admin dashboard</span>
            </button>
            <button
              className={`devtools-btn ${lightingPanelOpen ? 'devtools-btn--save' : 'devtools-btn--load'}`}
              onClick={() => setLightingPanelOpen(v => !v)}
            >
              💡 Lighting Panel
              <span className="devtools-btn-hint">
                {lightingPanelOpen ? 'Close lighting controls' : 'Adjust 3D scene lighting'}
              </span>
            </button>
            <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.15)', margin: '4px 0' }} />
            <button
              className="devtools-btn devtools-btn--dump"
              onClick={handleOpenBitmapVisualizer}
              disabled={!hasEstimate}
              style={!hasEstimate ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              🖼️ Bitmap Visualizer
              <span className="devtools-btn-hint">
                {hasEstimate ? 'Open PHP-rendered preview in new tab' : 'Calculate an estimate first'}
              </span>
            </button>
          </div>
          <div className="devtools-footer">
            Menu &rsaquo; <kbd>Dev Console</kbd> to toggle &middot; <kbd>Esc</kbd> to close
          </div>
        </div>
      )}
    </>
  );
};

